import {
  closeDialog,
  DataStatus,
  FormSubmitHandler,
  openDeleteDialog,
  openDialog,
} from 'platform/components';
import {Hide, VStack} from 'platform/foundation';

import {useReducer, useRef} from 'react';

import {add} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useDeleteServiceOrderJobMutation,
  useGetServiceCaseOrderJobsQuery,
  usePatchServiceOrderJobMutation,
  usePostServiceOrderJobMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';
import {handleApiError, queryParams} from '@dms/shared';
import {
  JobFormProps,
  JobInput,
  type JobInputForm,
  JobType,
  PostponeDialog,
} from '@dms/shared-aftersales';

import {composePath, Nullish, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {Job} from '../../../../../components/Job/Job';
import {JobSegments} from '../../../../../types/JobSegments';
import {CustomPrice} from './CustomPrice';
import {MoveRequest} from './MoveRequest';
import {OpenJobDetail} from './OpenJobDetail';

interface OrderJobsProps extends TestIdProps {
  orderId: string;
  orderVariantId?: string;
  serviceCaseId: string;
  isReadOnly?: boolean;
  datagridKey?: string;
  isReportButtonDisabled?: boolean;
}

export function OrderJobs(props: OrderJobsProps) {
  const [jobRefreshKey, reloadJob] = useReducer(add(1), 0);

  const navigate = useNavigate();

  const {data, isFetching, isError} = useGetServiceCaseOrderJobsQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
    },
    {
      selectFromResult: (result) => ({
        ...result,
        data: result.data?.filter((job) => !job?.isUncategorized || job?.hasItems),
      }),
    }
  );

  const [deleteServiceOrderJob] = useDeleteServiceOrderJobMutation();
  const [patchServiceOrderJob] = usePatchServiceOrderJobMutation();
  const [postServiceOrderJob] = usePostServiceOrderJobMutation();

  const knownJobIdsRef = useRef<Set<string> | null>(null);
  const jobIds = (data ?? []).map((job) => job?.id).filter((id): id is string => Boolean(id));
  const isEmpty = isNilOrEmpty(data);

  const onSubmit: FormSubmitHandler<JobInputForm> = async ({job}) => {
    await postServiceOrderJob({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      body: {name: job, orderVariantId: props?.orderVariantId ?? undefined},
    })
      .unwrap()
      .then((data) => {
        navigate(
          composePath(workshopRoutes.serviceCaseDetail, {
            params: {id: props.serviceCaseId},
            queryParams: {
              [queryParams.SERVICE_CASE_JOB_SEGMENT]: JobSegments.DETAILS,
              [queryParams.SERVICE_CASE_OPEN_JOB_ID]: data?.id ?? '',
              [queryParams.SERVICE_CASE_ORDER_ID]: props.orderId,
            },
          })
        );
      })
      .catch(handleApiError);
  };

  const handleUpdateJob: JobFormProps['onSubmit'] = async (data, reset) => {
    if (!data?.id) {
      return;
    }

    await patchServiceOrderJob({
      body: data,
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      serviceJobId: data?.id,
    })
      .unwrap()
      .then(() => {
        reset();
      })
      .catch(handleApiError);
  };

  const handleRemoveJob = (job: JobType | Nullish) => {
    if (!job?.id) {
      return;
    }

    openDeleteDialog({
      text: i18n.t('entity.orderRequest.actions.delete', {name: job?.name}),
      'data-testid': suffixTestId('removeJob', props),
      onConfirm: () =>
        deleteServiceOrderJob({
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.orderId,
          serviceJobId: job.id ?? '',
        })
          .unwrap()
          .catch(handleApiError),
    });
  };

  const handleMoveJob = (job: JobType | Nullish) => {
    if (!job?.id) {
      return;
    }

    openDialog(
      <MoveRequest
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.orderId}
        serviceJobId={job.id}
        onClose={() => closeDialog('moveJobDialog')}
        data-testid={suffixTestId('moveJobDialog', props)}
      />,
      {
        id: 'moveJobDialog',
        title: `${i18n.t('entity.orderRequest.actions.moveRequest')} (${job?.name})`,
      }
    );
  };

  const handlePostponeJob = (job: JobType | Nullish) => {
    if (!job?.id) {
      return;
    }

    openDialog(
      <PostponeDialog
        dialogId="postpone-dialog"
        jobsIds={job.id}
        postponeUntil={null}
        reason={null}
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.orderId}
        switchJobToPostponed
        data-testid={suffixTestId('postponeDialog', props)}
      />,
      {
        id: 'postpone-dialog',
        title: i18n.t('entity.serviceCase.labels.postpone'),
        size: 'small',
        withAdditionalFooter: true,
      }
    );
  };

  const onCustomPriceClose = () => {
    closeDialog('customPriceDialog');
    reloadJob();
  };

  const handleCustomPrice = (job: JobType | Nullish) => {
    if (!job) {
      return;
    }

    openDialog(
      <CustomPrice
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.orderId}
        job={job}
        onClose={onCustomPriceClose}
      />,
      {
        id: 'customPriceDialog',
        title: i18n.t('entity.orderRequest.actions.customPrice'),
      }
    );
  };

  return (
    <>
      {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
      {!isFetching && !isNilOrEmpty(jobIds) && (
        <OpenJobDetail key={jobIds.join()} jobIds={jobIds} knownJobIdsRef={knownJobIdsRef} />
      )}
      <VStack spacing={4}>
        {/* Gating on isFetching alone would remount the job cards and their grids (T20-140688) */}
        <DataStatus
          isLoading={isFetching && isEmpty}
          isError={isError}
          isEmpty={isEmpty}
          minHeight={30}
          data-testid={suffixTestId('orders', props)}
          emptyMessage={i18n.t('entity.order.labels.emptyRequests')}
          emptySubheadline={i18n.t('entity.order.labels.emptyRequestsDescription')}
        >
          {data?.map((job, index) => (
            <Job
              key={`${job?.id ?? index}${jobRefreshKey}`}
              isReadOnly={props.isReadOnly}
              jobData={job}
              serviceCaseId={props.serviceCaseId}
              orderId={props.orderId}
              datagridKey={props.datagridKey}
              actions={[
                {
                  key: 'job-customPrice',
                  label: i18n.t('entity.orderRequest.actions.customPrice'),
                  onClick: () => handleCustomPrice(job),
                },
                {
                  key: 'job-postpone',
                  label: i18n.t('entity.orderRequest.actions.postpone'),
                  onClick: () => handlePostponeJob(job),
                },
                {
                  key: 'job-move',
                  label: i18n.t('entity.orderRequest.actions.moveRequest'),
                  onClick: () => handleMoveJob(job),
                },
                {
                  key: 'job-delete',
                  label: i18n.t('entity.orderRequest.actions.deleteRequest'),
                  onClick: () => handleRemoveJob(job),
                },
              ]}
              onSubmit={handleUpdateJob}
              isReportButtonDisabled={props.isReportButtonDisabled}
              data-testid={suffixTestId(`job-[${index}]`, props)}
            />
          ))}
        </DataStatus>
        <Hide when={props.isReadOnly}>
          <JobInput onSubmit={onSubmit} data-testid={suffixTestId('job', props)} />
        </Hide>
      </VStack>
    </>
  );
}
